import { useState, useContext, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { IFinancialOperation } from "dto/Banking/IBanking";
import { ItemKind } from "dto/IKindItems";
import { IAttachment } from "dto/IAttachment";
import { generateFinancialOperationMetricPdf } from "utils/generateFinancialOperationMetricPdf";
import CourtPortalService from "api/CourtPortalService";
import usePDF from "hooks/usePDF";
import KeysContext from "contexts/KeysContext";
import PDFViewer from "components/PDFViewer";
import PdfIcon from "assets/icons/pdf.svg?react";
import { LinkedInvoice } from "components/Registry/ContractorsRegister/ContractorDetailPanel";
import PostingSchemeEditor from "components/Registry/ContractorsRegister/PostingSchemeEditor";
import { useSettlements } from "api/accountingService";
import styles from "./styles.module.scss";

// ── Typy ─────────────────────────────────────────────────────────────────────

// LinkedInvoice jest importowany z ContractorDetailPanel (źródło prawdy)
// i re-eksportowany dla wstecznej kompatybilności.
export type { LinkedInvoice };

export interface ContractorSlideData {
  /** Id kontrahenta — potrzebne do edytora schematów księgowania. */
  id: string;
  acctCode: string;
  name: string;
  nip: string;
  bankAccount: string;
  docCount: number;
  kwotaWn: number;
  kwotaMa: number;
  saldo: number;
  invoices: LinkedInvoice[];
  bankOps: IFinancialOperation[];
}

interface Props {
  contractor: ContractorSlideData;
  wpkLookup: Map<string, string>;
  onClose: () => void;
}

// ── Formatowanie ─────────────────────────────────────────────────────────────

const fmtKwota = (v: number) =>
  new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const fmtAmt = (v: number, currency = "PLN") =>
  new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + " " + currency;

const plDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

function fmtIban(raw: string): string {
  if (!raw) return "";
  const digits = raw.replace(/^PL/i, "").replace(/\D/g, "");
  if (digits.length !== 26) return raw;
  const groups = digits.match(/.{1,4}/g) ?? [];
  return `PL ${groups.join(" ")}`;
}

// ── Schematy T-konta ──────────────────────────────────────────────────────────

interface AccountEntry { code: string; name: string; }

interface TAccountScheme {
  title: string;
  wn: AccountEntry[];
  ma: AccountEntry[];
}

const INVOICE_SCHEMES: Record<"Zakup" | "Sprzedaż" | "Rejestr", TAccountScheme> = {
  Zakup: {
    title: "Faktura zakupowa",
    wn: [
      { code: "402", name: "Usługi obce (wartość netto)" },
      { code: "221", name: "VAT naliczony" },
    ],
    ma: [{ code: "211", name: "Rozrachunki z dostawcami (brutto)" }],
  },
  Rejestr: {
    title: "Faktura / rachunek (rejestr)",
    wn: [
      { code: "402", name: "Usługi obce (wartość netto)" },
      { code: "221", name: "VAT naliczony" },
    ],
    ma: [{ code: "211", name: "Rozrachunki z dostawcami (brutto)" }],
  },
  Sprzedaż: {
    title: "Faktura sprzedażowa",
    wn: [{ code: "201", name: "Rozrachunki z odbiorcami (brutto)" }],
    ma: [
      { code: "700", name: "Przychody ze świadczonych usług (netto)" },
      { code: "222", name: "VAT należny" },
    ],
  },
};

const BANK_SCHEMES: Record<"Credit" | "Debit", TAccountScheme> = {
  Credit: {
    title: "Wpływ na rachunek bankowy",
    wn: [{ code: "131", name: "Rachunek bankowy" }],
    ma: [{ code: "201", name: "Rozrachunki z odbiorcami" }],
  },
  Debit: {
    title: "Wypływ z rachunku bankowego",
    wn: [{ code: "211", name: "Rozrachunki z dostawcami" }],
    ma: [{ code: "131", name: "Rachunek bankowy" }],
  },
};

function applyAcctCode(scheme: TAccountScheme, acctCode?: string): TAccountScheme {
  if (!acctCode) return scheme;
  const sub = (e: AccountEntry): AccountEntry => {
    if (e.code === "201") return { code: `201-${acctCode}`, name: "Rozrachunki z odbiorcami" };
    if (e.code === "211") return { code: `211-${acctCode}`, name: "Rozrachunki z dostawcami" };
    return e;
  };
  return { ...scheme, wn: scheme.wn.map(sub), ma: scheme.ma.map(sub) };
}

const TAccountCard = ({ scheme }: { scheme: TAccountScheme }) => (
  <div className={styles.tCard}>
    <div className={styles.tCardTitle}>{scheme.title}</div>
    <div className={styles.tGrid}>
      <div className={styles.tWn}>
        <div className={`${styles.tColHeader} ${styles.tWnHeader}`}>Wn</div>
        {scheme.wn.map((e) => (
          <div key={e.code} className={styles.tEntry}>
            <span className={`${styles.tCode} ${styles.tWnCode}`}>{e.code}</span>
            <span className={styles.tName}>{e.name}</span>
          </div>
        ))}
      </div>
      <div className={styles.tDivider} />
      <div className={styles.tMa}>
        <div className={`${styles.tColHeader} ${styles.tMaHeader}`}>Ma</div>
        {scheme.ma.map((e) => (
          <div key={e.code} className={styles.tEntry}>
            <span className={`${styles.tCode} ${styles.tMaCode}`}>{e.code}</span>
            <span className={styles.tName}>{e.name}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Etykiety odznak ───────────────────────────────────────────────────────────

const TYPE_BADGE: Record<string, string> = {
  Zakup:    styles.badgeZakup,
  Sprzedaż: styles.badgeSprzedaz,
  Rejestr:  styles.badgeRejestr,
};

// ── Komponent główny ──────────────────────────────────────────────────────────

const ContractorMetricSlide = ({ contractor, onClose }: Props) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    invoices: true, bankops: true, schemes: true, rozrachunki: true,
  });
  const { data: settlements = [] } = useSettlements(contractor.id, !!contractor.id);
  const settlement = settlements.find((s) => s.contractorId === contractor.id) ?? settlements[0];
  const toggleSection = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  const [pdfBlob,      setPdfBlob]      = useState<Blob | null>(null);
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);

  // Hooks do podglądu plików (wzorzec z Budget/InvoiceMetricSlide)
  const { mutateAsync: getKsefPreview } = CourtPortalService.usePreviewPdf();
  const { decryptPackage } = usePDF();
  const { keys, setKeys } = useContext(KeysContext);

  const saldo = contractor.saldo;
  const { invoices, bankOps } = contractor;

  // Ładujemy klucze obiektów faktur rejestrowych do KeysContext
  useEffect(() => {
    const allObjectKeys = invoices.flatMap((inv) => inv.objectKeys ?? []);
    if (allObjectKeys.length === 0) return;
    const missing = allObjectKeys.filter((k) => !keys.some((x) => x.Id === k.Id));
    if (missing.length > 0) setKeys([...keys, ...missing]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  const invoiceTypes = [...new Set(invoices.map((inv) => inv.type))] as ("Zakup" | "Sprzedaż" | "Rejestr")[];
  const bankDirections = [...new Set(bankOps.map((op) => op.Direction))] as ("Credit" | "Debit")[];

  const invoiceSchemes = invoiceTypes.map((t) => applyAcctCode(INVOICE_SCHEMES[t], contractor.acctCode));
  const bankSchemes    = bankDirections.map((d) => applyAcctCode(BANK_SCHEMES[d], contractor.acctCode));
  const allSchemes     = [...invoiceSchemes, ...bankSchemes];

  const handleInvoicePdf = async (inv: LinkedInvoice) => {
    if (pdfLoadingId) return;
    setPdfLoadingId(inv.id);
    try {
      // KSeF (Zakup/Sprzedaż lub Rejestr z KsefNumber) — wizualizacja z XSL przez BE
      if (inv.ksefNumber) {
        const res = await getKsefPreview({ id: inv.ksefNumber });
        setPdfBlob(new Blob([(res as { data: ArrayBuffer }).data], { type: "application/pdf" }));
        return;
      }
      // Faktura rejestrowa z dołączonym skanem — deszyfrowanie pliku
      if (inv.fileUri && inv.encryptionKeyId) {
        const attachment = {
          Id: inv.id,
          Uri: inv.fileUri,
          FileName: inv.fileName ?? "faktura.pdf",
          EncryptionKeyId: inv.encryptionKeyId,
          Kind: ItemKind.KSeFInvoice,
          ContentType: "application/pdf",
        } as unknown as IAttachment;
        const blob = await decryptPackage(attachment, ItemKind.KSeFInvoice, false);
        setPdfBlob(blob);
        return;
      }
      // Brak danych do podglądu
      alert("Brak pliku do podglądu dla tej faktury.");
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleBankOpPdf = async (op: IFinancialOperation) => {
    if (pdfLoadingId) return;
    setPdfLoadingId(op.Id);
    try {
      const blob = await generateFinancialOperationMetricPdf({
        id: op.Id,
        aspspName: op.AspspName,
        accountName: op.AccountName,
        accountIban: op.AccountIban,
        bookingDate: op.BookingDate,
        valueDate: op.ValueDate,
        direction: op.Direction,
        amount: op.Amount,
        currency: op.Currency,
        counterpartyName: op.CounterpartyName,
        counterpartyIban: op.CounterpartyIban,
        title: op.Title,
        status: op.Status,
        balanceAfter: op.BalanceAfter,
        bankTransactionCode: op.BankTransactionCode,
        reference: op.Reference,
      });
      setPdfBlob(blob);
    } finally {
      setPdfLoadingId(null);
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {pdfBlob && (
        <div className={styles.pdfPreview}>
          <PDFViewer
            file={pdfBlob}
            title="Dokument"
            showCloseButton
            onCloseButton={() => setPdfBlob(null)}
            showSignButton
          />
        </div>
      )}

      <div className={styles.panel}>
        {/* Nagłówek */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>
              {contractor.name || "— brak nazwy —"}
            </span>
            <span className={styles.headerSub}>{contractor.acctCode}</span>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.closeBtn} onClick={onClose} title="Zamknij">
              ✕
            </button>
          </div>
        </div>

        <div className={styles.body}>

          {/* Dane kontrahenta */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("info")}>
            <span>Dane kontrahenta</span>
            <FontAwesomeIcon className={styles.sectionChevron} icon={collapsed.info ? faChevronRight : faChevronDown} />
          </div>
          {!collapsed.info && (
            <div className={styles.metaCard}>
              <div className={styles.metaGrid}>
                <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                  <span className={styles.metaLabel}>Nazwa</span>
                  <span className={styles.metaValue}>
                    {contractor.name || <span className={styles.noData}>—</span>}
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Konto FK</span>
                  <span className={`${styles.metaValue} ${styles.monoValue}`}>{contractor.acctCode}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>NIP</span>
                  <span className={`${styles.metaValue} ${styles.monoValue}`}>
                    {contractor.nip || <span className={styles.noData}>—</span>}
                  </span>
                </div>
                {contractor.bankAccount && (
                  <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                    <span className={styles.metaLabel}>Nr rachunku bankowego</span>
                    <span className={`${styles.metaValue} ${styles.monoValue}`}>{fmtIban(contractor.bankAccount)}</span>
                  </div>
                )}
                <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                  <div className={styles.saldoRow}>
                    <div className={styles.saldoItem}>
                      <span className={styles.metaLabel}>Obrót Wn</span>
                      <span className={styles.saldoWn}>{fmtKwota(contractor.kwotaWn)}</span>
                    </div>
                    <div className={styles.saldoItem}>
                      <span className={styles.metaLabel}>Obrót Ma</span>
                      <span className={styles.saldoMa}>{fmtKwota(contractor.kwotaMa)}</span>
                    </div>
                    <div className={styles.saldoItem}>
                      <span className={styles.metaLabel}>Saldo</span>
                      <span className={saldo > 0 ? styles.saldoPosValue : saldo < 0 ? styles.saldoNegValue : styles.saldoZeroValue}>
                        {saldo === 0 ? "0,00" : (saldo > 0 ? "Wn " : "Ma ") + fmtKwota(Math.abs(saldo))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Faktury i rachunki */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("invoices")}>
            <span>Faktury i rachunki</span>
            <div className={styles.sectionRight}>
              {invoices.length > 0 && <span className={styles.sectionCount}>{invoices.length}</span>}
              <FontAwesomeIcon className={styles.sectionChevron} icon={collapsed.invoices ? faChevronRight : faChevronDown} />
            </div>
          </div>
          {!collapsed.invoices && (
            invoices.length === 0 ? (
              <div className={styles.noDecrees}>Brak powiązanych faktur.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Nr dokumentu</th>
                      <th style={{ textAlign: "right" }}>Kwota brutto</th>
                      <th>Typ</th>
                      <th style={{ width: 28 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>{plDate(inv.date)}</td>
                        <td className={styles.monoCell}>{inv.number || "—"}</td>
                        <td className={styles.amountCell}>
                          {inv.grossAmount ? fmtAmt(inv.grossAmount, inv.currency) : "—"}
                        </td>
                        <td>
                          <span className={`${styles.badge} ${TYPE_BADGE[inv.type] ?? ""}`}>
                            {inv.type}
                          </span>
                        </td>
                        <td>
                          <Tooltip title="Podgląd PDF" placement="left">
                            <button
                              className={styles.pdfBtn}
                              disabled={pdfLoadingId === inv.id}
                              onClick={() => handleInvoicePdf(inv)}
                            >
                              <PdfIcon className={styles.pdfIcon} />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Operacje finansowe */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("bankops")}>
            <span>Operacje finansowe</span>
            <div className={styles.sectionRight}>
              {bankOps.length > 0 && <span className={styles.sectionCount}>{bankOps.length}</span>}
              <FontAwesomeIcon className={styles.sectionChevron} icon={collapsed.bankops ? faChevronRight : faChevronDown} />
            </div>
          </div>
          {!collapsed.bankops && (
            bankOps.length === 0 ? (
              <div className={styles.noDecrees}>Brak powiązanych operacji bankowych.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th style={{ textAlign: "right" }}>Kwota</th>
                      <th>Kierunek</th>
                      <th style={{ width: 28 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {bankOps.map((op) => {
                      const isCredit = op.Direction === "Credit";
                      return (
                        <tr key={op.Id}>
                          <td>{plDate(op.BookingDate ?? "")}</td>
                          <td className={`${styles.amountCell} ${isCredit ? styles.amountCredit : styles.amountDebit}`}>
                            {isCredit ? "+" : "−"}{fmtAmt(op.Amount, op.Currency)}
                          </td>
                          <td>
                            <span className={`${styles.badge} ${isCredit ? styles.badgeWplyw : styles.badgeWyplyw}`}>
                              {isCredit ? "Wpływ" : "Wypływ"}
                            </span>
                          </td>
                          <td>
                            <Tooltip title="Podgląd PDF" placement="left">
                              <button
                                className={styles.pdfBtn}
                                disabled={pdfLoadingId === op.Id}
                                onClick={() => handleBankOpPdf(op)}
                              >
                                <PdfIcon className={styles.pdfIcon} />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Schematy księgowania */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("schemes")}>
            <span>Schematy księgowania</span>
            <div className={styles.sectionRight}>
              {allSchemes.length > 0 && <span className={styles.sectionCount}>{allSchemes.length}</span>}
              <FontAwesomeIcon className={styles.sectionChevron} icon={collapsed.schemes ? faChevronRight : faChevronDown} />
            </div>
          </div>
          {!collapsed.schemes && (
            <>
              {allSchemes.length > 0 && (
                <div className={styles.schemeList}>
                  {allSchemes.map((s) => (
                    <TAccountCard key={s.title} scheme={s} />
                  ))}
                </div>
              )}
              {/* Edytowalne, przechowywane w BE schematy księgowania kontrahenta
                  (wywiedzione z dziennika / definiowane i aktualizowane ręcznie). */}
              <PostingSchemeEditor contractorId={contractor.id} />
            </>
          )}

          {/* Rozrachunki (z zaksięgowanych dekretów na kontach 20x/21x) */}
          <div className={styles.sectionTitle} onClick={() => toggleSection("rozrachunki")}>
            <span>Rozrachunki (z księgowań)</span>
            <div className={styles.sectionRight}>
              <FontAwesomeIcon className={styles.sectionChevron} icon={collapsed.rozrachunki ? faChevronRight : faChevronDown} />
            </div>
          </div>
          {!collapsed.rozrachunki && (
            settlement ? (
              <div style={{ display: "flex", gap: 10, padding: "4px 4px 6px 22px" }}>
                {[
                  { label: "Obroty Wn", value: fmtKwota(settlement.totalWn) },
                  { label: "Obroty Ma", value: fmtKwota(settlement.totalMa) },
                  {
                    label: settlement.saldo >= 0 ? "Saldo (należność)" : "Saldo (zobowiązanie)",
                    value: fmtKwota(Math.abs(settlement.saldo)),
                  },
                ].map((m) => (
                  <div
                    key={m.label}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: "8px 10px",
                      background: "#fff",
                      border: "1px solid #e8e4f4",
                      borderRadius: 6,
                    }}
                  >
                    <span style={{ fontSize: 11, color: "#8a7fa5" }}>{m.label}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1630" }}>{m.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noDecrees}>Brak zaksięgowanych rozrachunków.</div>
            )
          )}

        </div>
      </div>
    </div>
  );
};

export default ContractorMetricSlide;
